import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { Combobox } from '@headlessui/react';
import { ReactNode, Ref, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon } from '@scale/scaleui';

/**
 * This is a fork of @scale/scaleui's Autocomplete component.
 *
 * We forked it to support multiple option selecting. i.e. autocompleting from a list of options and selecting multiple options.
 * Fork changes are marked with comments starting with [fork].
 */

const DEFAULT_AUTOCOMPLETE_PROPS = {
  className: '',
  label: '',
  value: '',
  disabled: false,
  canCreateOption: true,
};

// TODO: Add the missing props
type ComboboxProps = {
  options: string[];

  // [fork] modified this prop to accept multiple values
  setSelectedOptions: (values: string[]) => void;

  leadingIcon?: FontAwesomeIconProps['icon'];
  trailingIcon?: FontAwesomeIconProps['icon'];
  // React versioning issue: 'Type 'LegacyRef<HTMLInputElement> | undefined' is not assignable to type 'Ref<HTMLInputElement> | undefined'
  // For now, cast to proper ref type
  optionClassName?: string;
  optionBoxClassName?: string;
  onInputChange?: (a: string) => void;
  inputProps?: Omit<React.ComponentProps<'input'>, 'ref'> & {
    ref?: Ref<HTMLInputElement> | undefined;
  };
  renderOption?: (option: string, selected: boolean) => ReactNode;
  canCreateOption?: boolean;
  // No need to document the following props, they are documented in the globalTypes section of .storybook/preview.tsx
  className?: string;
  label?: string;
  disabled?: boolean;

  /**
   * [fork] modified this prop to accept multiple values
   * also added getLabelForOption
   * */
  values: string[];
  getLabelForOption?: (option: string) => string;
};

/**
 * This component was created using this
 * [TODO: Add Figma link]
 * mockup as reference
 */

const AutocompleteInput = ({
  options,
  setSelectedOptions,
  className,
  label,
  disabled,
  values,
  leadingIcon,
  trailingIcon,
  onInputChange,
  renderOption,
  optionClassName,
  optionBoxClassName,
  inputProps,
  getLabelForOption,
  canCreateOption = DEFAULT_AUTOCOMPLETE_PROPS.canCreateOption,
}: ComboboxProps) => {
  const [query, setQuery] = useState('');

  const filteredOptions =
    query === ''
      ? options
      : options.filter(option => {
          const optionLower = getLabelForOption
            ? getLabelForOption(option).toLowerCase()
            : option.toLowerCase();

          return optionLower.includes(query.toLowerCase());
        });

  // [fork] added this function to toggle multiple options
  const toggleOption = (option: string) => {
    if (values.includes(option)) {
      setSelectedOptions(values.filter(value => value !== option));
    } else {
      setSelectedOptions([...values, option]);
    }
  };

  /** [fork]
   * added multiple to Combobox and  modified the onChange prop to setSelectedOptions
   * also changed Combobox.Option to use toggleOption
   * */
  return (
    <div className={twMerge('scaleui relative', className)}>
      <Combobox multiple value={values} onChange={setSelectedOptions} disabled={disabled}>
        {({ open }) => (
          <>
            {label && (
              <Combobox.Label
                className={twMerge(
                  'block mb-1 text-sm font-medium',
                  disabled ? 'cursor-not-allowed text-neutral-500' : 'text-neutral-800',
                )}
              >
                {label}
              </Combobox.Label>
            )}
            <Combobox.Button
              as="div"
              className={twMerge(
                'relative w-full py-2 pl-3 pr-10 text-left border rounded-lg shadow-sm cursor-default focus:outline-none focus:ring-1 sm:text-sm bg-neutral-0 focus:ring-primary-500 border-neutral-300',
                disabled && 'cursor-not-allowed bg-neutral-50',
              )}
            >
              <div className="flex">
                {leadingIcon && (
                  <span className="pr-2 pointer-events-none">
                    <Icon icon={leadingIcon} />
                  </span>
                )}
                <Combobox.Input
                  {...inputProps}
                  displayValue={(values: string[]) => `(${values.length}) ${values.join(', ')}`}
                  className={twMerge(
                    'relative w-full p-0 shadow-none sm:text-sm cursor-default border-none focus:ring-0',
                    disabled
                      ? 'cursor-not-allowed bg-neutral-50 text-neutral-500'
                      : 'text-neutral-700',
                    inputProps?.className,
                  )}
                  unselectable={disabled ? 'on' : 'off'}
                  onChange={event => {
                    setQuery(event.target.value);
                    onInputChange?.(event.target.value);
                  }}
                />
                {trailingIcon && (
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <Icon icon={trailingIcon} />
                  </span>
                )}
              </div>
            </Combobox.Button>
            {open && (
              <Combobox.Options
                static
                className={twMerge(
                  'absolute z-20 w-auto min-w-full max-w-sm py-1 mt-1 overflow-auto text-base rounded-lg shadow-lg bg-neutral-0 max-h-60 ring-1 ring-neutral-900 ring-opacity-5 focus:outline-none sm:text-sm dark:[color-scheme:dark]',
                  optionBoxClassName,
                )}
              >
                {query.length > 0 && !options.includes(query) && canCreateOption && (
                  <Combobox.Option key="create" value={query}>
                    {({ active }) => (
                      <p
                        className={twMerge(
                          'text-neutral-900 p-2 cursor-pointer',
                          active ? 'bg-primary-200' : 'bg-neutral-200',
                        )}
                      >
                        Create "{query}"
                      </p>
                    )}
                  </Combobox.Option>
                )}
                {filteredOptions.map(option => (
                  <Combobox.Option
                    key={option}
                    value={option}
                    onClick={() => toggleOption(option)}
                    className={twMerge(
                      'relative select-none py-2 pl-3 pr-9 text-neutral-800 bg-neutral-0 cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-200 group transition',
                      optionClassName,
                    )}
                  >
                    {({ selected }) => (
                      <>
                        {renderOption ? (
                          renderOption(option, selected)
                        ) : (
                          <p
                            className={twMerge(
                              selected ? 'font-semibold' : 'font-normal',
                              'block truncate scale-90 origin-left transition-transform group-hover:scale-100',
                            )}
                          >
                            {getLabelForOption ? getLabelForOption(option) : option}
                          </p>
                        )}
                        {selected && (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                            <Icon icon={['fas', 'check']} variant="primary" />
                          </span>
                        )}
                      </>
                    )}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            )}
          </>
        )}
      </Combobox>
    </div>
  );
};

export default AutocompleteInput;
